import { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase/config';
import { getUserDoc, ensureUserDocument } from '@/lib/firebase/auth';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';

/**
 * AuthProvider listens to Firebase Auth state changes and syncs
 * the Firestore user document into Zustand. Renders children immediately
 * (loading handled at the route level via ProtectedRoute).
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setFirebaseUid, setUser, setInitialized } = useAuthStore();
  const { setIsOnline } = useAppStore();

  useEffect(() => {
    // Sync online/offline status
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);

    // Firebase Auth state listener
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setFirebaseUid(firebaseUser.uid);
        // Ensure a Firestore user document exists (handles first-time Google sign-ins)
        await ensureUserDocument(firebaseUser, []);
        const userDoc = await getUserDoc(firebaseUser.uid);
        setUser(userDoc);
      } else {
        setFirebaseUid(null);
        setUser(null);
      }
      setInitialized(true);
    });

    return () => {
      unsubscribeAuth();
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [setFirebaseUid, setUser, setInitialized, setIsOnline]);

  return <>{children}</>;
}
